import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ProductCard from '../components/common/ProductCard';
import CategoryBrandExplorer from '../components/products/CategoryBrandExplorer';
import productService from '../services/productService';
import categoryService, { buildCategoryHierarchy } from '../services/categoryService';
import { useRealtimeSync } from '../hooks/useRealtimeSync';
import { getImageUrl } from '../utils/imageUrl';
import { FiSearch, FiX, FiLoader, FiAlertCircle, FiArrowLeft, FiChevronRight } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { BUSINESS_WHATSAPP_CHAT_URL } from '../config/businessInfo';
import './ProductsPage.css';

const SORT_OPTIONS = [
    { value: 'default',          label: 'Default (Newest)' },
    { value: 'name_asc',         label: 'Name: A to Z' },
    { value: 'price_asc',        label: 'MRP: Low to High' },
    { value: 'price_desc',       label: 'MRP: High to Low' },
    { value: 'installment_asc',  label: 'MRP (installment sort): Low to High' },
    { value: 'installment_desc', label: 'MRP (installment sort): High to Low' },
];

const LIMIT = 12;

const ALL_PARTNER_BRANDS = [
    'Dawlance', 'PEL', 'Orient', 'TCL', 'Gree', 'Samsung', 'Waves', 'Super Asia', 'Eco Star',
    'Multynet', 'Konvo', 'United', 'Boss', 'Care', 'Enviro', 'Sabro', 'iZone', 'F&D'
];

const ProductsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const search = searchParams.get('search') || '';

    /* ── State ─────────────────────────────────────────────── */
    const [products,       setProducts]       = useState([]);
    const [categories,     setCategories]     = useState([]);
    const [activeParentId, setActiveParentId] = useState(() => searchParams.get('parent') || null);
    const [selectedCat,    setSelectedCat]    = useState(() => searchParams.get('category') || 'all');
    const [sortBy,         setSortBy]         = useState('default');
    const [searchInput,    setSearchInput]    = useState(() => searchParams.get('search') || '');

    const [page,           setPage]           = useState(1);
    const [hasMore,        setHasMore]        = useState(false);
    const [totalCount,     setTotalCount]     = useState(0);

    const [loadingInit,    setLoadingInit]    = useState(true);   // first fetch / filter change
    const [loadingMore,    setLoadingMore]    = useState(false);  // "load more" fetch
    const [catLoading,     setCatLoading]     = useState(true);
    const [catError,       setCatError]       = useState('');
    const [error,          setError]          = useState('');

    /* ── Refs ───────────────────────────────────────────────── */
    const sentinelRef  = useRef(null);   // intersection observer target
    const debounceRef  = useRef(null);
    const prevUrlSearchRef = useRef(search);
    const isMountedRef = useRef(true);
    const lastCatFetchRef = useRef(0);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    /* ── Fetch categories & keep synced with admin dashboard ── */
    const loadCategories = useCallback(async ({ silent = false, forceRefresh = false } = {}) => {
        try {
            if (!silent && isMountedRef.current) {
                setCatLoading(true);
                setCatError('');
            }
            const cats = await categoryService.getAll({}, { forceRefresh });
            if (!isMountedRef.current) return;
            setCategories(Array.isArray(cats) ? cats : []);
            setCatError('');
            lastCatFetchRef.current = Date.now();
        } catch (err) {
            if (!isMountedRef.current) return;
            if (!silent) {
                setCatError(err?.message || 'Failed to load categories. Please try again.');
            }
        } finally {
            if (!silent && isMountedRef.current) {
                setCatLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (
                document.visibilityState === 'visible' &&
                Date.now() - lastCatFetchRef.current > 3000
            ) {
                loadCategories({ silent: true, forceRefresh: true });
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [loadCategories]);

    /* Sync toolbar when URL search changes (header, back/forward, links) — not on redundant updates */
    useEffect(() => {
        if (prevUrlSearchRef.current !== search) {
            prevUrlSearchRef.current = search;
            setSearchInput(search);
        }
    }, [search]);

    const hierarchy = useMemo(() => buildCategoryHierarchy(categories), [categories]);

    /* Sync category & parent hierarchy state when URL params or categories change */
    useEffect(() => {
        const rawCat = searchParams.get('category');
        const rawParent = searchParams.get('parent');

        if (rawCat && rawCat !== 'all') {
            if (categories.length > 0) {
                const matched = categories.find(
                    (c) =>
                        String(c._id) === String(rawCat) ||
                        c.name?.toLowerCase() === rawCat.toLowerCase() ||
                        (c.slug && c.slug.toLowerCase() === rawCat.toLowerCase())
                );
                if (matched) {
                    if (hierarchy.hasSubcategories(matched._id)) {
                        // Selected URL category is a parent with subcategories: reveal its subcategories
                        setActiveParentId(String(matched._id));
                        setSelectedCat('all');
                    } else {
                        // Subcategory or leaf parent category: filter products & open parent if applicable
                        setSelectedCat(String(matched._id));
                        setActiveParentId(matched.parent_id ? String(matched.parent_id) : null);
                    }
                    return;
                }
            }
            setSelectedCat(rawCat);
            return;
        }

        setSelectedCat('all');

        if (rawParent) {
            if (categories.length > 0) {
                const matchedParent = categories.find(
                    (c) =>
                        String(c._id) === String(rawParent) ||
                        c.name?.toLowerCase() === rawParent.toLowerCase()
                );
                if (matchedParent && hierarchy.hasSubcategories(matchedParent._id)) {
                    setActiveParentId(String(matchedParent._id));
                    return;
                }
                setActiveParentId(null);
            } else {
                setActiveParentId(rawParent);
            }
        } else {
            setActiveParentId(null);
        }
    }, [searchParams, categories, hierarchy]);

    const selectedBrand = searchParams.get('brand') || 'all';

    /* ── Core fetch ─────────────────────────────────────────── */
    const fetchProducts = useCallback(async (pageNum, append = false) => {
        try {
            if (append) setLoadingMore(true);
            else        setLoadingInit(true);
            setError('');

            const { products: newProducts, pagination } = await productService.getAll(
                pageNum, LIMIT, search, selectedCat, sortBy, '', false, selectedBrand
            );
            console.log("products", newProducts)

            setProducts(prev => append ? [...prev, ...newProducts] : newProducts);
            setHasMore(pagination.hasNextPage || false);
            setTotalCount(pagination.totalCount || 0);
            setPage(pageNum);
        } catch (err) {
            setError(err.message || 'Failed to load products. Please try again.');
        } finally {
            setLoadingInit(false);
            setLoadingMore(false);
        }
    }, [search, selectedCat, sortBy, selectedBrand]);

    /* Reset to page 1 whenever filters / sort / search change */
    useEffect(() => {
        fetchProducts(1, false);
    }, [fetchProducts]);

    // Real-time synchronization: refresh product catalog, prices, and categories automatically
    useRealtimeSync('products', useCallback(() => {
        fetchProducts(1, false);
    }, [fetchProducts]));

    useRealtimeSync('prices', useCallback(() => {
        fetchProducts(1, false);
    }, [fetchProducts]));

    useRealtimeSync('categories', useCallback(() => {
        loadCategories({ silent: true, forceRefresh: true });
    }, [loadCategories]));

    /* ── Infinite scroll via IntersectionObserver ───────────── */
    useEffect(() => {
        if (!sentinelRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loadingInit) {
                    fetchProducts(page + 1, true);
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, loadingInit, page, fetchProducts]);

    /* ── Debounced search ───────────────────────────────────── */
    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchInput(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            const q = val.trim();
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (q) next.set('search', q);
                    else next.delete('search');
                    return next;
                },
                { replace: true }
            );
        }, 600);
    };

    const clearSearch = () => {
        setSearchInput('');
        clearTimeout(debounceRef.current);
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete('search');
                return next;
            },
            { replace: true }
        );
    };

    /* ── Category & Subcategory select ──────────────────────── */
    const handleCategoryClick = (catId) => {
        if (!catId || catId === 'all') {
            setActiveParentId(null);
            setSelectedCat('all');
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('category');
                    next.delete('parent');
                    next.delete('brand');
                    return next;
                },
                { replace: true }
            );
            return;
        }

        const resolvedId = String(catId);
        const catObj = hierarchy.getCategoryById(resolvedId);

        if (hierarchy.hasSubcategories(resolvedId)) {
            // Parent category with subcategories: reveal subcategories instead of filtering empty parent
            setActiveParentId(resolvedId);
            setSelectedCat('all');
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('parent', resolvedId);
                    next.delete('category');
                    next.delete('brand');
                    return next;
                },
                { replace: true }
            );
        } else {
            // Subcategory or leaf parent category: filter products directly
            const parentId = catObj?.parent_id ? String(catObj.parent_id) : null;
            setActiveParentId(parentId);
            setSelectedCat(resolvedId);
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('category', resolvedId);
                    next.delete('parent');
                    next.delete('brand');
                    return next;
                },
                { replace: true }
            );
        }
    };

    const handleNavigateParent = (targetParentId = null) => {
        const nextParent = targetParentId ? String(targetParentId) : null;
        setActiveParentId(nextParent);
        setSelectedCat('all');
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (nextParent) {
                    next.set('parent', nextParent);
                } else {
                    next.delete('parent');
                }
                next.delete('category');
                next.delete('brand');
                return next;
            },
            { replace: true }
        );
    };

    /* ── Brand select ────────────────────────────────────────── */
    const handleBrandClick = (brandName) => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (brandName && brandName !== 'all') {
                    next.set('brand', brandName);
                } else {
                    next.delete('brand');
                }
                return next;
            },
            { replace: true }
        );
    };

    /* ── Sort change ────────────────────────────────────────── */
    const handleSortChange = (e) => {
        setSortBy(e.target.value);
    };

    /* ── Render ─────────────────────────────────────────────── */
    return (
        <div className='products-page'>
            {/* Header */}
            <div className='page-header'>
                <div className='container'>
                    <div style={{ marginBottom: '0.85rem' }}>
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="product-page-back-btn"
                            aria-label="Go back"
                        >
                            <FiArrowLeft /> Back
                        </button>
                    </div>
                    <h1>Our Products</h1>
                    <p>Explore our wide range of home appliances</p>
                </div>
            </div>

            <div className='container'>
                {/* ── Interactive Category & Brand Explorer (Al-Mumtaz Style) ── */}
                <CategoryBrandExplorer
                    categories={categories}
                    hierarchy={hierarchy}
                    activeParentId={activeParentId}
                    loading={catLoading}
                    error={catError}
                    onRetry={() => loadCategories({ silent: false, forceRefresh: true })}
                    selectedCat={selectedCat}
                    selectedBrand={selectedBrand}
                    onSelectCategory={handleCategoryClick}
                    onNavigateParent={handleNavigateParent}
                    onSelectBrand={handleBrandClick}
                />

                <div className='products-layout'>
                    {/* ── Sidebar ── */}
                    <aside className='products-sidebar'>
                        <div className='filter-section'>
                            <h3>
                                {activeParentId && hierarchy.getCategoryById(activeParentId)
                                    ? hierarchy.getCategoryById(activeParentId).name
                                    : 'Categories'}
                            </h3>
                            {catLoading ? (
                                <div className='sidebar-loading'>
                                    <FiLoader className='spin-icon' />
                                    <span>Loading…</span>
                                </div>
                            ) : (
                                <div className='filter-options'>
                                    {activeParentId && hierarchy.getCategoryById(activeParentId) ? (
                                        <>
                                            <button
                                                type='button'
                                                className='filter-btn filter-btn--back'
                                                onClick={() => {
                                                    const currentParent =
                                                        hierarchy.getCategoryById(activeParentId);
                                                    handleNavigateParent(
                                                        currentParent?.parent_id || null
                                                    );
                                                }}
                                            >
                                                <span className='filter-btn-back-inner'>
                                                    <FiArrowLeft aria-hidden='true' />
                                                    <span>Main Categories</span>
                                                </span>
                                            </button>

                                            {hierarchy
                                                .getSubcategories(activeParentId)
                                                .map((sub) => {
                                                    const subChildrenCount =
                                                        hierarchy.getSubcategories(sub._id).length;
                                                    const isSubActive =
                                                        String(selectedCat) === String(sub._id);
                                                    return (
                                                        <button
                                                            key={sub._id}
                                                            type='button'
                                                            className={`filter-btn ${
                                                                isSubActive ? 'active' : ''
                                                            }`}
                                                            onClick={() =>
                                                                handleCategoryClick(sub._id)
                                                            }
                                                        >
                                                            <span>{sub.name}</span>
                                                            {isSubActive ? (
                                                                <span className='cat-count'>
                                                                    {totalCount}
                                                                </span>
                                                            ) : subChildrenCount > 0 ? (
                                                                <FiChevronRight
                                                                    className='sidebar-cat-chevron'
                                                                    aria-hidden='true'
                                                                />
                                                            ) : null}
                                                        </button>
                                                    );
                                                })}
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type='button'
                                                className={`filter-btn ${
                                                    selectedCat === 'all' ? 'active' : ''
                                                }`}
                                                onClick={() => handleCategoryClick('all')}
                                            >
                                                <span>All Products</span>
                                                {selectedCat === 'all' && (
                                                    <span className='cat-count'>{totalCount}</span>
                                                )}
                                            </button>

                                            {hierarchy.parentCategories.map((cat) => {
                                                const subCount = hierarchy.getSubcategories(
                                                    cat._id
                                                ).length;
                                                const isCatActive =
                                                    String(selectedCat) === String(cat._id);
                                                return (
                                                    <button
                                                        key={cat._id}
                                                        type='button'
                                                        className={`filter-btn ${
                                                            isCatActive ? 'active' : ''
                                                        }`}
                                                        onClick={() => handleCategoryClick(cat._id)}
                                                    >
                                                        <span>{cat.name}</span>
                                                        {isCatActive ? (
                                                            <span className='cat-count'>
                                                                {totalCount}
                                                            </span>
                                                        ) : subCount > 0 ? (
                                                            <span className='sidebar-sub-indicator'>
                                                                <span className='cat-count'>
                                                                    {subCount}
                                                                </span>
                                                                <FiChevronRight
                                                                    className='sidebar-cat-chevron'
                                                                    aria-hidden='true'
                                                                />
                                                            </span>
                                                        ) : null}
                                                    </button>
                                                );
                                            })}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </aside>

                    {/* ── Main content ── */}
                    <main className='products-main'>
                        {/* Toolbar */}
                        <div className='products-toolbar'>
                            {/* Search */}
                            <div className='search-wrapper'>
                                <FiSearch className='search-icon' />
                                <input
                                    type='text'
                                    className='search-input'
                                    placeholder='Search products…'
                                    value={searchInput}
                                    onChange={handleSearchChange}
                                />
                                {searchInput && (
                                    <button className='search-clear' onClick={clearSearch} title='Clear search'>
                                        <FiX />
                                    </button>
                                )}
                            </div>

                            {/* Sort + count */}
                            <div className='toolbar-right'>
                                {!loadingInit && (
                                    <p className='results-count'>
                                        {totalCount} product{totalCount !== 1 ? 's' : ''}
                                    </p>
                                )}
                                <select
                                    className='sort-select'
                                    value={sortBy}
                                    onChange={handleSortChange}
                                >
                                    {SORT_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* ── Official Brand Filter Bar (Almumtaz Style) ── */}
                        <div className="products-brand-filter-bar">
                            <span className="brand-filter-title">Brand:</span>
                            <div className="brand-filter-scroll">
                                <button
                                    type="button"
                                    className={`brand-filter-pill ${selectedBrand === 'all' ? 'active' : ''}`}
                                    onClick={() => handleBrandClick('all')}
                                >
                                    All Brands
                                </button>
                                {ALL_PARTNER_BRANDS.map((b) => (
                                    <button
                                        key={b}
                                        type="button"
                                        className={`brand-filter-pill ${selectedBrand.toLowerCase() === b.toLowerCase() ? 'active' : ''}`}
                                        onClick={() => handleBrandClick(b)}
                                    >
                                        {b}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Error banner */}
                        {error && (
                            <div className='products-error'>
                                <FiAlertCircle />
                                <span>{error}</span>
                            </div>
                        )}

                        {/* Initial loading skeleton */}
                        {loadingInit ? (
                            <div className='products-grid'>
                                {[...Array(LIMIT)].map((_, i) => (
                                    <div key={i} className='product-skeleton' />
                                ))}
                            </div>
                        ) : products.length === 0 ? (
                            <div className='products-empty'>
                                <FiAlertCircle className='empty-icon' />
                                <p>No products found.</p>
                                {(search || selectedCat !== 'all') && (
                                    <button
                                        className='btn btn-outline'
                                        onClick={() => { clearSearch(); handleCategoryClick('all'); }}
                                    >
                                        Clear filters
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className='products-grid'>
                                    {products.map((product) => (
                                        <ProductCard
                                            key={product._id}
                                            product={{
                                                ...product,
                                                image: getImageUrl(product.image),
                                                _imagePath: product.image,
                                            }}
                                        />
                                    ))}
                                </div>

                                {/* Sentinel for intersection observer */}
                                <div ref={sentinelRef} className='scroll-sentinel' />

                                {/* Loading more spinner */}
                                {loadingMore && (
                                    <div className='load-more-spinner'>
                                        <FiLoader className='spin-icon' />
                                        <span>Loading more products…</span>
                                    </div>
                                )}

                                {/* Custom Catalog Inquiry Bar */}
                                {!hasMore && !loadingMore && products.length > 0 && (
                                    <div className='catalog-inquiry-box'>
                                        <div>
                                            <h4>Looking for another model, capacity, or brand?</h4>
                                            <p>We stock additional appliances across our Lahore showroom and wholesale warehouse. Inquire for current availability and bulk discount rates.</p>
                                        </div>
                                        <a
                                            href={BUSINESS_WHATSAPP_CHAT_URL}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='catalog-inquiry-wa'
                                        >
                                            <FaWhatsapp /> Check Stock on WhatsApp
                                        </a>
                                    </div>
                                )}
                            </>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default ProductsPage;
